import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, SlidersHorizontal, ChevronDown, X, Star, ArrowUpDown } from "lucide-react";
import Navbar from "../../components/Header";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const API    = "https://api.komorotel.com";

const TYPE_LABELS = {
  hotel:               "Hôtel",
  chambre_hote:        "Chambre d'hôte",
  appartement_studio:  "Appartement & Studio",
};

export default function HotelList() {
  const location = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(location.search);

  const destination = params.get("destination") || "";
  const checkIn     = params.get("checkIn")     || "";
  const checkOut    = params.get("checkOut")    || "";
  const adults   = params.get("adults")   || "";
  const children = params.get("children") || "";
  const hasDates    = checkIn && checkOut;

  /* ── State ── */
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [sortOrder,    setSortOrder]    = useState("");
  const [maxPrice,     setMaxPrice]     = useState(1000);
  const [selectedType, setSelectedType] = useState("");
  const [showFilters,  setShowFilters]  = useState(false); // mobile sheet

  /* ── Fetch ── */
  useEffect(() => {
    let url = `${API}/api/hotels/search?destination=${destination}`;
    if (checkIn && checkOut) url += `&checkIn=${checkIn}&checkOut=${checkOut}`;
    if (adults)   url += `&adults=${adults}`;
    if (children) url += `&children=${children}`;

    setLoading(true);
    fetch(url)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [location.search]);

  /* ── Filtrage + tri ── */
  const hotels = (data || [])
    .filter(h => h.minPrice <= maxPrice && (!selectedType || h.type === selectedType))
    .sort((a, b) => {
      if (sortOrder === "asc")  return a.minPrice - b.minPrice;
      if (sortOrder === "desc") return b.minPrice - a.minPrice;
      return 0;
    });

  const activeFilters = (selectedType ? 1 : 0) + (maxPrice < 1000 ? 1 : 0);

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Navbar />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "88px 32px 48px", display: "flex", gap: 28 }}>

        {/* ── SIDEBAR DESKTOP ── */}
        <aside style={{ width: 260, flexShrink: 0, display: "none", alignSelf: "flex-start", position: "sticky", top: 88 }}
          className="sidebar-desktop">
          <FilterPanel
            maxPrice={maxPrice} setMaxPrice={setMaxPrice}
            selectedType={selectedType} setSelectedType={setSelectedType}
            onReset={() => { setMaxPrice(1000); setSelectedType(""); }}
          />
        </aside>

        {/* ── MAIN ── */}
        <main style={{ flex: 1, minWidth: 0 }}>

          {/* Header résultats */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                Hôtels à <span style={{ color: G }}>{destination || "Toutes les destinations"}</span>
              </h1>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                {loading ? "Recherche en cours..." : `${hotels.length} hébergement${hotels.length > 1 ? "s" : ""} trouvé${hotels.length > 1 ? "s" : ""}`}
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {/* Bouton filtres mobile */}
              <button onClick={() => setShowFilters(true)}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "9px 14px", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer", position: "relative" }}>
                <SlidersHorizontal size={15} />
                Filtres
                {activeFilters > 0 && (
                  <span style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: G, color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {activeFilters}
                  </span>
                )}
              </button>

              {/* Tri */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "9px 12px" }}>
                <ArrowUpDown size={14} color="#6b7280" />
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                  style={{ border: "none", outline: "none", fontSize: 13, fontWeight: 500, color: "#374151", background: "transparent", cursor: "pointer" }}>
                  <option value="">Trier par</option>
                  <option value="asc">Prix croissant</option>
                  <option value="desc">Prix décroissant</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── Empty state ── */}
          {!loading && hotels.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, marginBottom: 16 }}>😕</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
                {hasDates ? "Aucun hôtel disponible" : "Aucun résultat trouvé"}
              </h2>
              <p style={{ fontSize: 14, color: "#6b7280", maxWidth: 360, lineHeight: 1.6, marginBottom: 24 }}>
                {hasDates
                  ? "Aucun hébergement n'est disponible pour ces dates. Essayez de modifier vos dates."
                  : "Aucun hébergement ne correspond à vos filtres."}
              </p>
              <button onClick={() => navigate("/")}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Modifier ma recherche
              </button>
            </div>
          )}

          {/* ── Grille hôtels ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
            {loading
              ? [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
              : hotels.map(hotel => (
                  <HotelCard key={hotel._id} hotel={hotel}
                    queryString={hasDates ? `?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults || 1}&children=${children || 0}` : ""} />
                ))
            }
          </div>
        </main>
      </div>

      {/* ── BOTTOM SHEET FILTRES MOBILE ── */}
      {showFilters && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500 }}>
          <div onClick={() => setShowFilters(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: "#fff", borderRadius: "20px 20px 0 0",
            padding: "0 24px 32px", maxHeight: "85vh", overflowY: "auto",
          }}>
            <div style={{ width: 40, height: 4, background: "#d1d5db", borderRadius: 2, margin: "14px auto 20px" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700 }}>Filtres</h2>
              <button onClick={() => setShowFilters(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280" }}>
                <X size={20} />
              </button>
            </div>
            <FilterPanel
              maxPrice={maxPrice} setMaxPrice={setMaxPrice}
              selectedType={selectedType} setSelectedType={setSelectedType}
              onReset={() => { setMaxPrice(1000); setSelectedType(""); }}
            />
            <button onClick={() => setShowFilters(false)}
              style={{ width: "100%", marginTop: 20, background: G, color: "#fff", border: "none", borderRadius: 12, padding: "14px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Voir les résultats ({hotels.length})
            </button>
          </div>
        </div>
      )}

      {/* CSS responsive sidebar */}
      <style>{`
        @media (min-width: 1024px) { .sidebar-desktop { display: block !important; } }
      `}</style>
    </div>
  );
}

/* ── Card hôtel ── */
function HotelCard({ hotel, queryString }) {
  const [hovered, setHovered] = useState(false);
  const [liked,   setLiked]   = useState(false);

  return (
    <a href={`/hotel/slug/${hotel.slug}${queryString}`}
      style={{ textDecoration: "none", display: "block", borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #f3f4f6", boxShadow: hovered ? "0 6px 24px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow 0.2s, transform 0.2s", transform: hovered ? "translateY(-2px)" : "none" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {/* Image */}
      <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
        <img src={hotel.photos?.[0]} alt={hotel.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s", transform: hovered ? "scale(1.05)" : "scale(1)" }} />
        <button onClick={e => { e.preventDefault(); setLiked(!liked); }}
          style={{ position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.88)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill={liked ? "#ef4444" : "none"} stroke={liked ? "#ef4444" : "#6b7280"} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
        {hotel.type && (
          <div style={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 5, backdropFilter: "blur(4px)" }}>
            {TYPE_LABELS[hotel.type] || hotel.type}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>{hotel.name}</h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <MapPin size={12} color="#9ca3af" />
          <span style={{ fontSize: 12, color: "#9ca3af" }}>{hotel.city}</span>
        </div>
        {hotel.desc && (
          <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {hotel.desc}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #f9fafb", paddingTop: 10 }}>
          <div>
            <span style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>
              {hotel.minPrice ? `${hotel.minPrice}€` : "—"}
            </span>
            <span style={{ fontSize: 11, color: "#9ca3af" }}> / nuit</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: G }}>Voir →</span>
        </div>
      </div>
    </a>
  );
}

/* ── Panneau filtres (réutilisé desktop + mobile) ── */
function FilterPanel({ maxPrice, setMaxPrice, selectedType, setSelectedType, onReset }) {
  const types = [
    { value: "",                    label: "Tous" },
    { value: "hotel",               label: "Hôtel" },
    { value: "appartement_studio",  label: "Appartement & Studio" },
    { value: "chambre_hote",        label: "Chambre d'hôte" },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f3f4f6", padding: "20px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Filtres</h3>
        <button onClick={onReset} style={{ background: "none", border: "none", fontSize: 12, color: G, fontWeight: 600, cursor: "pointer" }}>
          Réinitialiser
        </button>
      </div>

      {/* Prix max */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Prix max / nuit</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: G }}>{maxPrice}€</span>
        </div>
        <input type="range" min={50} max={1000} value={maxPrice}
          onChange={e => setMaxPrice(Number(e.target.value))}
          style={{ width: "100%", accentColor: G }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>50€</span>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>1000€</span>
        </div>
      </div>

      {/* Type */}
      <div>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 10 }}>Type</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {types.map(t => (
            <label key={t.value} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              <div onClick={() => setSelectedType(t.value)}
                style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${selectedType === t.value ? G : "#d1d5db"}`, background: selectedType === t.value ? G : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
                {selectedType === t.value && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
              </div>
              {t.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Skeleton ── */
function SkeletonCard() {
  return (
    <div style={{ borderRadius: 14, overflow: "hidden", background: "#fff", border: "1px solid #f3f4f6" }}>
      <div style={{ height: 200, background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }} />
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ height: 14, background: "#f3f4f6", borderRadius: 6, marginBottom: 8, width: "70%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, marginBottom: 12, width: "45%" }} />
        <div style={{ height: 12, background: "#f3f4f6", borderRadius: 6, width: "90%" }} />
      </div>
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
    </div>
  );
}